import { INPUT_STYLES } from '../styles';

export interface IFormFieldSetters {
  setCardNumberPreview: React.Dispatch<React.SetStateAction<string>>;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCardNumberValid: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCvvValid: React.Dispatch<React.SetStateAction<boolean>>;
  errors: spreedly.SpreedlyError[];
  setErrors: React.Dispatch<React.SetStateAction<spreedly.SpreedlyError[]>>;
  acceptedBins: string[];
}

const ERROR_CENCOSUD = 'La tarjeta no es Cencosud Scotiabank';

const checkAcceptedBins = (iin: string, acceptedBins: string[]): boolean => {
  if (acceptedBins.length === 0) {
    return true;
  }
  return acceptedBins.some((bin) => iin.startsWith(bin));
};

const setFieldStyle = (name: spreedly.SpreedlyField, style: string) => {
  Spreedly.setStyle(name, style);
};

const handleFocus = (name: spreedly.SpreedlyField) => {
  setFieldStyle(name, INPUT_STYLES.focusNumber);
};

const handleBlur = (
  name: spreedly.SpreedlyField,
  inputData?: spreedly.SpreedlyFieldEventInputProperties
) => {
  const isValid =
    (name === 'number' && inputData?.validNumber) ||
    (name === 'cvv' && inputData?.validCvv);

  setFieldStyle(name, isValid ? INPUT_STYLES.valid : INPUT_STYLES.blurNumber);
};

const clearCencosudError = (seters: IFormFieldSetters) => {
  seters.setErrors(
    seters.errors.filter((error) => error.message !== ERROR_CENCOSUD)
  );
};

const handleNumberInput = (
  inputData: spreedly.SpreedlyFieldEventInputProperties,
  seters: IFormFieldSetters
) => {
  if (inputData.numberLength === 0) {
    setFieldStyle('number', INPUT_STYLES.default);
    clearCencosudError(seters);
    return;
  }

  const isNumberValid = inputData?.validNumber ?? false;

  if (!inputData.iin) {
    clearCencosudError(seters);
    return;
  }

  const hasAcceptedBins = checkAcceptedBins(
    inputData.iin ?? '',
    seters.acceptedBins
  );

  if (hasAcceptedBins) {
    seters.setIsCardNumberValid(isNumberValid);
    setFieldStyle('number', INPUT_STYLES.valid);
    const hasCencosudError = seters.errors.some(
      (error) => error.message === ERROR_CENCOSUD
    );

    if (hasCencosudError) {
      clearCencosudError(seters);
    }
  } else {
    setFieldStyle('number', INPUT_STYLES.errorNumber);
    seters.setErrors([
      {
        attribute: 'number',
        message: ERROR_CENCOSUD,
        key: 'errors.invalid'
      }
    ]);
    return false;
  }
};

const handleInput = (
  name: spreedly.SpreedlyField,
  seters: IFormFieldSetters,
  inputData: spreedly.SpreedlyFieldEventInputProperties | undefined
) => {
  if (!inputData) return;

  const isNumberValid = (name === 'number' && inputData?.validNumber) ?? false;
  const isCvvValid = (name === 'cvv' && inputData?.validCvv) ?? false;

  seters.setIsCvvValid(isCvvValid);

  if (name === 'number') {
    handleNumberInput(inputData, seters);
  }

  if (isNumberValid) {
    Spreedly.validate();
  }

  setFieldStyle(
    name,
    isNumberValid ? INPUT_STYLES.valid : INPUT_STYLES.default
  );
};

const printMask = (numberOfDigits: number) => {
  return '*'.repeat(numberOfDigits);
};

const handleFieldEvent = (
  name: spreedly.SpreedlyField,
  event: string,
  activeElement: string,
  seters: IFormFieldSetters,
  inputData?: spreedly.SpreedlyFieldEventInputProperties | undefined
) => {
  if (!name || !event) return;

  if (name === 'number' && event === 'input' && activeElement === 'number') {
    const maskedNumber = printMask(inputData?.numberLength ?? 0);
    seters.setCardNumberPreview(maskedNumber);
  }

  const handlers = {
    focus: () => {
      seters.setIsFocused(true);
      handleFocus(name);
    },
    blur: () => {
      seters.setIsFocused(false);
      handleBlur(name, inputData);
    },
    input: () => {
      handleInput(name, seters, inputData);
    }
  };

  const handler = handlers[event as keyof typeof handlers];
  if (handler) {
    handler();
  }
};

export default handleFieldEvent;
